import { reportApi } from '../../../../api/reportApi';
import { DEFAULT_REPORT_MODEL } from './reportSettings';

// Default model for report generation. Users can switch to Mistral in the config modal.
export const REPORT_LLM_MODEL = DEFAULT_REPORT_MODEL;

// Fallback/default values. The live list comes from /api/reports/config.
export const AVAILABLE_MODELS = [
  {
    value: REPORT_LLM_MODEL,
    label: 'Claude Sonnet 4.6',
    provider: 'Anthropic',
    description: 'Default. Anthropic model used for report generation.',
    isDefault: true,
    contextWindow: '1M tokens',
    maxOutput: '128k tokens',
    inputPrice: '$3 / 1M',
    outputPrice: '$15 / 1M',
    capabilities: ['text', 'vision', 'tools']
  },
  {
    value: 'mistral-medium-3.5',
    label: 'Mistral Medium 3.5',
    provider: 'Mistral',
    description: 'Frontier Mistral model. Input and output share the context window.',
    isDefault: false,
    contextWindow: '256k tokens',
    maxOutput: 'shares the 256k window',
    inputPrice: '$1.50 / 1M',
    outputPrice: '$7.50 / 1M',
    capabilities: ['text', 'reasoning', 'vision', 'tools']
  },
  {
    value: 'mistral-large-4',
    label: 'Mistral Large 4 (preview)',
    provider: 'Mistral',
    description: 'Newest Mistral flagship, 49B active / 1T total parameters. Public preview.',
    isDefault: false,
    contextWindow: '512k tokens',
    maxOutput: 'shares the 512k window',
    inputPrice: '$1.36 / 1M',
    outputPrice: '$4.18 / 1M',
    capabilities: ['text', 'reasoning', 'vision', 'tools']
  },
  {
    value: 'mistral-large-2512',
    label: 'Mistral Large 3',
    provider: 'Mistral',
    description: 'General-purpose open-weight model, 41B active / 675B total parameters.',
    isDefault: false,
    contextWindow: '256k tokens',
    maxOutput: 'shares the 256k window',
    inputPrice: '$0.50 / 1M',
    outputPrice: '$1.50 / 1M',
    capabilities: ['text', 'vision', 'tools']
  },
  {
    value: 'mistral-small-2603',
    label: 'Mistral Small 4',
    provider: 'Mistral',
    description: 'Self-hosted Mistral-Small-4 (NVFP4) at mistral.diplomacy.edu.',
    isDefault: false,
    contextWindow: '256k tokens',
    maxOutput: 'shares the 256k window',
    inputPrice: '$0.15 / 1M',
    outputPrice: '$0.60 / 1M',
    capabilities: ['text', 'reasoning', 'vision', 'tools']
  },
  {
    value: 'zai-glm-5-3',
    label: 'Z.ai GLM 5.3',
    provider: 'Mistral',
    description: 'Third-party text model hosted on the Mistral API.',
    isDefault: false,
    contextWindow: '1M tokens',
    maxOutput: 'shares the 1M window',
    inputPrice: '$1.40 / 1M',
    outputPrice: '$4.40 / 1M',
    capabilities: ['text', 'reasoning', 'tools']
  },
  {
    value: 'ministral-14b-2512',
    label: 'Ministral 3 14B',
    provider: 'Mistral',
    description: 'Compact Mistral model. Cheaper, less capacity than Medium or Large.',
    isDefault: false,
    contextWindow: '256k tokens',
    maxOutput: 'shares the 256k window',
    inputPrice: '$0.20 / 1M',
    outputPrice: '$0.20 / 1M',
    capabilities: ['text', 'vision', 'tools']
  },
  {
    value: 'ministral-8b-2512',
    label: 'Ministral 3 8B',
    provider: 'Mistral',
    description: 'Smaller Ministral model for shorter sessions.',
    isDefault: false,
    contextWindow: '256k tokens',
    maxOutput: 'shares the 256k window',
    inputPrice: '$0.15 / 1M',
    outputPrice: '$0.15 / 1M',
    capabilities: ['text', 'vision', 'tools']
  },
  {
    value: 'ministral-3b-2512',
    label: 'Ministral 3 3B',
    provider: 'Mistral',
    description: 'Smallest Ministral model on this key. API context is 131,072 tokens.',
    isDefault: false,
    contextWindow: '128k tokens',
    maxOutput: 'shares the 128k window',
    inputPrice: '$0.10 / 1M',
    outputPrice: '$0.10 / 1M',
    capabilities: ['text', 'vision', 'tools']
  }
];

// Models that are no longer offered but still appear on older reports.
const RETIRED_MODEL_LABELS = {
  'openai/gpt-oss-120b': 'GPT-OSS 120B (Local)',
  'gpt-5.4': 'GPT-5.4 (disabled)',
  'gpt-4o': 'GPT-4o (disabled)',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4 (legacy)',
};

export const reportModelLabel = (model) => (
  AVAILABLE_MODELS.find((m) => m.value === model)?.label || RETIRED_MODEL_LABELS[model] || model
);

/** Editable prompts, their defaults and the models, from GET /api/reports/config. */
export const loadReportConfig = async () => {
  const config = await reportApi.getReportConfig();
  return {
    editablePrompts: config.editable_prompts.map((p) => ({
      key: p.key,
      name: p.name,
      description: p.description,
      requiredPlaceholders: p.required_placeholders,
    })),
    availableModels: config.available_models.map((m) => ({
      value: m.value,
      label: m.label,
      provider: m.provider,
      description: m.description,
      isDefault: m.is_default,
      contextWindow: m.context_window,
      maxOutput: m.max_output,
      inputPrice: m.input_price,
      outputPrice: m.output_price,
      capabilities: m.capabilities || [],
    })),
    defaultPrompts: config.default_prompts,
  };
};
